import { Filter, ChevronRight, RotateCcw, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { TruncatedCell } from "@/components/shared/TruncatedCell"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import FilterList, { type AgeGroup, SPECIES_CONFIG } from "./FilterList"


interface AppliedFilterChip {
  key: string
  label: string
  onRemove: () => void
}

interface FilterPanelProps {
  errorModeActive: boolean
  onResetFilters: () => void
  /** Currently selected species values. */
  selectedSpecies: string[]
  /** Callback when species selection changes. */
  onSpeciesChange: (species: string[]) => void
  /** All unique method values derived from the loaded dataset. */
  methodOptions: string[]
  /** Currently selected method values. */
  selectedMethods: string[]
  /** Callback when method selection changes. */
  onMethodsChange: (methods: string[]) => void
  /** All unique source_id1 values derived from the loaded dataset. */
  sourceIdOptions: string[]
  /** Currently selected source IDs to filter by. */
  selectedSourceIds: string[]
  /** Callback when source ID selection changes. */
  onSourceIdsChange: (sourceIds: string[]) => void
  /** All unique map_survey_id values derived from the loaded dataset. */
  mapSurveyIdOptions: string[]
  /** Currently selected MAP survey IDs to filter by. */
  selectedMapSurveyIds: string[]
  /** Callback when MAP survey ID selection changes. */
  onMapSurveyIdsChange: (mapSurveyIds: string[]) => void
  /** Whether only survey data should be shown (toggle on = survey only). */
  onlySurveyData: boolean
  /** Callback when the survey-only toggle changes. */
  onSurveyDataToggle: (value: boolean) => void
  /** Currently selected age-group categories (Under 5 / Over 5 / Mixed). */
  selectedAgeGroups: AgeGroup[]
  /** Callback when the age-group selection changes. */
  onAgeGroupsChange: (ageGroups: AgeGroup[]) => void
  /** All distinct validation_error values in the loaded failure dataset. */
  validationErrorOptions: string[]
  /** Currently selected validation-error values to narrow the failures by. */
  selectedValidationErrors: string[]
  /** Callback when the validation-error selection changes. */
  onValidationErrorsChange: (validationErrors: string[]) => void
}

export default function FilterPanel({
  errorModeActive,
  onResetFilters,
  selectedSpecies,
  onSpeciesChange,
  methodOptions,
  selectedMethods,
  onMethodsChange,
  sourceIdOptions,
  selectedSourceIds,
  onSourceIdsChange,
  mapSurveyIdOptions,
  selectedMapSurveyIds,
  onMapSurveyIdsChange,
  onlySurveyData,
  onSurveyDataToggle,
  selectedAgeGroups,
  onAgeGroupsChange,
  validationErrorOptions,
  selectedValidationErrors,
  onValidationErrorsChange,
}: FilterPanelProps): React.JSX.Element {

  function removeFromArray(
    values: string[],
    valueToRemove: string,
    onChange: (next: string[]) => void,
  ): void {
    onChange(values.filter((value) => value !== valueToRemove))
  }

  const allSpeciesLabels: string[] = SPECIES_CONFIG.map((species) => species.label)
  const isDefaultSpecies: boolean =
    selectedSpecies.length === allSpeciesLabels.length

  const appliedChips: AppliedFilterChip[] = []

  if (!isDefaultSpecies) {
    appliedChips.push({
      key: "species",
      label: `Species: ${selectedSpecies.length} of ${allSpeciesLabels.length}`,
      onRemove: () => onSpeciesChange([...allSpeciesLabels]),
    })
  }

  for (const method of selectedMethods) {
    appliedChips.push({
      key: `method:${method}`,
      label: method,
      onRemove: () => removeFromArray(selectedMethods, method, onMethodsChange),
    })
  }

  for (const ageGroup of selectedAgeGroups) {
    appliedChips.push({
      key: `age:${ageGroup}`,
      label: ageGroup,
      onRemove: () =>
        onAgeGroupsChange(selectedAgeGroups.filter((value) => value !== ageGroup)),
    })
  }

  for (const sourceId of selectedSourceIds) {
    appliedChips.push({
      key: `source:${sourceId}`,
      label: sourceId,
      onRemove: () => removeFromArray(selectedSourceIds, sourceId, onSourceIdsChange),
    })
  }

  for (const surveyId of selectedMapSurveyIds) {
    appliedChips.push({
      key: `survey:${surveyId}`,
      label: surveyId,
      onRemove: () =>
        removeFromArray(selectedMapSurveyIds, surveyId, onMapSurveyIdsChange),
    })
  }

  if (onlySurveyData) {
    appliedChips.push({
      key: "survey-only",
      label: "Survey data only",
      onRemove: () => onSurveyDataToggle(false),
    })
  }

  if (errorModeActive) {
    for (const validationError of selectedValidationErrors) {
      appliedChips.push({
        key: `verr:${validationError}`,
        label: validationError,
        onRemove: () =>
          removeFromArray(
            selectedValidationErrors,
            validationError,
            onValidationErrorsChange,
          ),
      })
    }
  }

  return (
    <Collapsible className="w-full overflow-hidden rounded-xl border border-border/60 bg-background/80 shadow-xl ring-1 ring-black/5 backdrop-blur-md">
      <div className="flex w-full items-center gap-2 px-4 py-2">
        <CollapsibleTrigger className="group flex flex-1 items-center gap-2 text-left transition-colors hover:text-foreground">
          <Filter className="size-3.5 text-muted-foreground -mt-0.5" aria-hidden="true" />
          <span className="flex items-baseline gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-foreground/80">
            Filters
          </span>
          <ChevronRight className="size-3.5 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-90" />
        </CollapsibleTrigger>
        <Button
          variant="ghost"
          size="sm"
          className="h-5 shrink-0 gap-1.5 text-xs text-foreground/80"
          onClick={onResetFilters}
        >
          <RotateCcw className="size-3.5" aria-hidden="true" />
          Reset
        </Button>
      </div>

      <CollapsibleContent>
        <div className="flex flex-col gap-3 border-t border-border/60 px-4 pb-4 pt-3 overflow-auto max-h-65">
          {appliedChips.length > 0 && (
            <ul
              className="flex flex-row flex-wrap gap-1.5 max-w-[90%]"
              aria-label="Applied filters"
            >
              {appliedChips.map(chip => (
                <li key={chip.key} className="flex min-w-0 max-w-full">
                  <Badge variant="secondary" className="max-w-full gap-1 px-1">
                    <button
                      type="button"
                      onClick={chip.onRemove}
                      aria-label={`Remove filter: ${chip.label}`}
                      className="inline-flex size-4 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    >
                      <X className="size-3" aria-hidden="true" />
                    </button>
                    <TruncatedCell value={chip.label} className="min-w-0 flex-1" />
                  </Badge>
                </li>
              ))}
            </ul>
          )}
          <FilterList
            selectedSpecies={selectedSpecies}
            onSpeciesChange={onSpeciesChange}
            methodOptions={methodOptions}
            selectedMethods={selectedMethods}
            onMethodsChange={onMethodsChange}
            sourceIdOptions={sourceIdOptions}
            selectedSourceIds={selectedSourceIds}
            onSourceIdsChange={onSourceIdsChange}
            mapSurveyIdOptions={mapSurveyIdOptions}
            selectedMapSurveyIds={selectedMapSurveyIds}
            onMapSurveyIdsChange={onMapSurveyIdsChange}
            onlySurveyData={onlySurveyData}
            onSurveyDataToggle={onSurveyDataToggle}
            selectedAgeGroups={selectedAgeGroups}
            onAgeGroupsChange={onAgeGroupsChange}
            errorModeActive={errorModeActive}
            validationErrorOptions={validationErrorOptions}
            selectedValidationErrors={selectedValidationErrors}
            onValidationErrorsChange={onValidationErrorsChange}
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
